import type { ActivityLog } from '@/types'
import { formatInvoiceAmount } from './invoiceTabStyles'

function numberProp(
  properties: ActivityLog['properties'],
  key: string
): number | null {
  const value = properties?.[key]
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() && Number.isFinite(+value)) {
    return +value
  }
  return null
}

export function activityTitle(
  item: ActivityLog,
  t: (key: string, options?: Record<string, unknown>) => string
) {
  const amount = numberProp(item.properties, 'amount')

  switch (item.event_type) {
    case 'invoice.paid':
      return t('detail.history.events.invoicePaid')
    case 'payment.received':
    case 'payment.onsite_cash':
    case 'payment.onsite_qr':
      return amount !== null
        ? t('detail.history.events.paymentReceivedWithAmount', {
            amount: formatInvoiceAmount(amount),
          })
        : t('detail.history.events.paymentReceived')
    case 'payment.pending':
      return amount !== null
        ? t('detail.history.events.paymentPendingWithAmount', {
            amount: formatInvoiceAmount(amount),
          })
        : t('detail.history.events.paymentPending')
    case 'invoice.created':
      return t('detail.history.events.invoiceCreated')
    case 'invoice.updated':
      return item.description === 'Invoice validated'
        ? t('detail.history.events.invoiceValidated')
        : t('detail.history.events.invoiceUpdated')
    case 'invoice.sent':
      return t('detail.history.events.invoiceSent')
    case 'invoice.note_added':
      return t('detail.history.events.noteAdded')
    case 'invoice.cancelled':
      return t('detail.history.events.invoiceCancelled')
    case 'invoice.auto_generated':
      return t('detail.history.events.invoiceAutoGenerated')
    case 'invoice.reminder_sent':
      return t('detail.history.events.invoiceReminderSent')
    case 'credit_note.created':
      return t('detail.history.events.creditNoteCreated')
    case 'credit_note.updated':
      return t('detail.history.events.creditNoteUpdated')
    case 'credit_note.sent':
      return t('detail.history.events.creditNoteSent')
    case 'credit_note.deleted':
      return t('detail.history.events.creditNoteDeleted')
    case 'payment.created':
      return amount !== null
        ? t('detail.history.events.paymentCreatedWithAmount', {
            amount: formatInvoiceAmount(amount),
          })
        : t('detail.history.events.paymentCreated')
    case 'payment.status_changed':
      return t('detail.history.events.paymentStatusChanged')
    case 'payment.refunded':
      return t('detail.history.events.paymentRefunded')
    case 'payment.failed':
      return t('detail.history.events.paymentFailed')
    case 'payment.proof_uploaded':
      return t('detail.history.events.paymentProofUploaded')
    case 'payment.superseded':
      return t('detail.history.events.paymentSuperseded')
    case 'payment.reminder_internal':
    case 'payment.reminder_client':
      return t('detail.history.events.paymentReminderSent')
    default:
      return (
        item.description?.trim() || item.event_type.replace(/[._]/g, ' ').trim()
      )
  }
}
